<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed, onMounted } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import LocalIcon from '@/atoms/LocalIcon.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';

export type BaseCheckboxProps = {
  id: string | number;
  initialValue?: boolean;
  class?: string;
  indeterminate?: boolean;
  size?: 'medium';
  colorMode?: 'default' | 'on-brand';
  state?: 'default' | 'disabled' | 'hover';
  title?: string;
  description?: string;
};

const baseCheckbox = cva('flex rounded-md w-5 h-5 bg-white relative mt-0.5', {
  variants: {
    state: {
      default: 'border border-primary',
      hover: 'border border-primary bg-grey-25',
      disabled: 'border border-primary bg-disabled-subtle text-brand-solid',
    },
    checked: {
      true: '',
      false: '',
    },
    indeterminate: {
      true: '',
      false: '',
    },
    colorMode: {
      default: '',
      'on-brand': '',
    },
    size: {
      medium: '',
    },
  },
  compoundVariants: [
    {
      checked: true,
      colorMode: 'default',
      state: 'default',
      class: 'bg-brand-solid text-white border-0',
    },
    {
      checked: true,
      colorMode: 'default',
      state: 'hover',
      class: 'bg-blue-900 text-white border-0',
    },
    {
      checked: true,
      colorMode: 'on-brand',
      state: 'default',
      class: 'bg-white border-0 text-brand-solid',
    },
    {
      checked: true,
      colorMode: 'on-brand',
      state: 'hover',
      class: 'bg-grey-25 border-0 text-brand-solid',
    },
  ],
  defaultVariants: {
    state: 'default',
    size: 'medium',
    colorMode: 'default',
  },
});

const props = defineProps<BaseCheckboxProps>();
const isChecked = defineModel<boolean>();

onMounted(() => {
  if (isChecked.value === undefined) {
    isChecked.value = props.initialValue;
  }
});

const classes = computed(() =>
  cn(
    baseCheckbox({
      ...props,
      checked: isChecked.value,
    }),
    props.class,
  ),
);
</script>

<template>
  <div class="flex space-x-2">
    <div :class="classes">
      <input
        :data-cy="id"
        :id="String(id)"
        :disabled="state === 'disabled'"
        type="checkbox"
        class="appearance-none w-full h-full min-w-5 min-h-5.5 z-10"
        v-model="isChecked"
      />
      <div v-if="isChecked" class="absolute inset-0 flex items-center justify-center">
        <LocalIcon v-if="indeterminate" size="small" name="minus" />
        <LocalIcon v-else size="small" name="check" />
      </div>
    </div>
    <div class="flex flex-col text-primary">
      <BaseLabel v-if="title" :text="title" preset="medium" color="primary" />
      <BaseLabel v-if="description" :text="description" preset="small" color="secondary" />
      <slot v-if="!title && !description" />
    </div>
  </div>
</template>
